import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  ICreateUpdateDailyCheckDefinitionRequest,
  ICreateUpdateDailyCheckStepRequest,
  IDailyCheckDefinitionsResponse,
  IDailyChecksResponse,
  ISingleDailyCheckDefinitionResponse,
  ISingleDailyCheckStepResponse,
} from "../models/DailyChecks";

// includeDeleted is for pages rendering past days: results outlive the definition they were recorded
// against, so retired names are needed to label them. Editing surfaces should leave it off.
export default function useDailyChecks(date?: string, includeDeleted: boolean = false) {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const definitions = useQuery({
    queryKey: ["daily-check-definitions", includeDeleted],
    queryFn: () => get<IDailyCheckDefinitionsResponse>(`/dailycheck/definitions?activeOnly=false&includeDeleted=${includeDeleted}`),
    staleTime: 1000 * 60 * 10,
  });

  const checksForDate = useQuery({
    queryKey: ["daily-checks", date],
    queryFn: () => get<IDailyChecksResponse>(`/dailycheck/date/${date}`),
    enabled: !!date,
    staleTime: 1000 * 60,
  });

  const createDefinition = useMutation(
    (body: ICreateUpdateDailyCheckDefinitionRequest) => post<ICreateUpdateDailyCheckDefinitionRequest, ISingleDailyCheckDefinitionResponse>("/dailycheck/definitions", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["daily-check-definitions"]);
      },
    }
  );

  const updateDefinition = useMutation(
    (body: ICreateUpdateDailyCheckDefinitionRequest) => put<ICreateUpdateDailyCheckDefinitionRequest, ISingleDailyCheckDefinitionResponse>(`/dailycheck/definitions/${body.id}`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["daily-check-definitions"]);
      },
    }
  );

  const updateDefinitionOrder = useMutation(
    ({ definitionId, newIndex }: { definitionId: string, newIndex: number }) => put(`/dailycheck/definitions/${definitionId}/order?newIndex=${newIndex}`, undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["daily-check-definitions"]);
      },
    }
  );

  const deleteDefinition = useMutation(
    (definitionId: string) => del(`/dailycheck/definitions/${definitionId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["daily-check-definitions"]);
      },
    }
  );

  const createStep = useMutation(
    (body: ICreateUpdateDailyCheckStepRequest) => post<ICreateUpdateDailyCheckStepRequest, ISingleDailyCheckStepResponse>("/dailycheck/steps", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["daily-check-definitions"]);
      },
    }
  );

  const updateStep = useMutation(
    (body: ICreateUpdateDailyCheckStepRequest) => put<ICreateUpdateDailyCheckStepRequest, ISingleDailyCheckStepResponse>(`/dailycheck/steps/${body.id}`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["daily-check-definitions"]);
      },
    }
  );

  const updateStepOrder = useMutation(
    ({ stepId, newIndex }: { stepId: string, newIndex: number }) => put(`/dailycheck/steps/${stepId}/order?newIndex=${newIndex}`, undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["daily-check-definitions"]);
      },
    }
  );

  const deleteStep = useMutation(
    (stepId: string) => del(`/dailycheck/steps/${stepId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["daily-check-definitions"]);
      },
    }
  );

  return {
    definitions,
    checksForDate,
    createDefinition,
    updateDefinition,
    updateDefinitionOrder,
    deleteDefinition,
    createStep,
    updateStep,
    updateStepOrder,
    deleteStep,
  };
}
